import { isOcpp2 } from "./protocol";
import type { ChargePointSimulator } from "./simulator";

// Realistic example requests for the CSMS messages people test most, filled from the simulator's
// own state (id tag, running transaction, ...). The upload / download targets are public echo
// endpoints that accept the request and allow CORS, so a real transfer can be tried right away.
export const EXAMPLE_UPLOAD_URL = "https://httpbin.org/post";
export const EXAMPLE_FIRMWARE_URL = "https://httpbin.org/anything/evsafe-firmware-1.2.0.bin";

export const exampleCsmsRequest = (sim: ChargePointSimulator, action: string): any => {
  const now = new Date(sim.timestamp());
  const inMinutes = (minutes: number) => new Date(now.getTime() + minutes * 60_000).toISOString();
  const evse = sim.evses.find(e => e.transaction) ?? sim.evses[0];
  const tx = evse?.transaction;
  const idTag = sim.settings.idTag;
  const idToken = { idToken: idTag, type: "ISO14443" };

  if (!isOcpp2(sim.protocol)) {
    switch (action) {
      case "GetDiagnostics":
        return { location: EXAMPLE_UPLOAD_URL, retries: 1, retryInterval: 10 };
      case "GetLog":
        return { logType: "DiagnosticsLog", requestId: 1, retries: 1, retryInterval: 10, log: { remoteLocation: EXAMPLE_UPLOAD_URL } };
      case "UpdateFirmware":
        return { location: EXAMPLE_FIRMWARE_URL, retrieveDate: now.toISOString(), retries: 1, retryInterval: 10 };
      case "SignedUpdateFirmware":
        return {
          requestId: 1,
          retries: 1,
          retryInterval: 10,
          firmware: {
            location: EXAMPLE_FIRMWARE_URL,
            retrieveDateTime: now.toISOString(),
            signingCertificate: "-----BEGIN CERTIFICATE-----\nMIIBEVSAFE\n-----END CERTIFICATE-----",
            signature: "c2lnbmF0dXJl",
          },
        };
      case "RemoteStartTransaction":
        return { connectorId: evse?.id ?? 1, idTag };
      case "RemoteStopTransaction":
        return { transactionId: tx?.numericId ?? 0 };
      case "Reset":
        return { type: "Soft" };
      case "ChangeConfiguration":
        return { key: "MeterValueSampleInterval", value: "30" };
      case "GetConfiguration":
        return { key: ["HeartbeatInterval", "MeterValueSampleInterval", "SupportedFeatureProfiles"] };
      case "ChangeAvailability":
        return { connectorId: evse?.id ?? 1, type: "Inoperative" };
      case "UnlockConnector":
        return { connectorId: evse?.id ?? 1 };
      case "TriggerMessage":
        return { requestedMessage: "StatusNotification", connectorId: evse?.id ?? 1 };
      case "ExtendedTriggerMessage":
        return { requestedMessage: "LogStatusNotification" };
      case "ReserveNow":
        return { connectorId: evse?.id ?? 1, expiryDate: inMinutes(15), idTag, reservationId: 1 };
      case "CancelReservation":
        return { reservationId: evse?.reservation?.id ?? 1 };
      case "SetChargingProfile":
        return {
          connectorId: evse?.id ?? 1,
          csChargingProfiles: {
            chargingProfileId: 1,
            stackLevel: 0,
            chargingProfilePurpose: tx ? "TxProfile" : "TxDefaultProfile",
            chargingProfileKind: "Relative",
            ...(tx?.numericId !== undefined && { transactionId: tx.numericId }),
            chargingSchedule: { chargingRateUnit: "A", chargingSchedulePeriod: [{ startPeriod: 0, limit: 10, numberPhases: 3 }] },
          },
        };
      case "GetCompositeSchedule":
        return { connectorId: evse?.id ?? 1, duration: 3600, chargingRateUnit: "W" };
      case "SendLocalList":
        return {
          listVersion: sim.localList.version + 1,
          updateType: "Full",
          localAuthorizationList: [{ idTag, idTagInfo: { status: "Accepted", expiryDate: inMinutes(60 * 24 * 365) } }],
        };
      case "DataTransfer":
        return { vendorId: "EVsafe", messageId: "Ping", data: "{}" };
      default:
        return undefined;
    }
  }

  switch (action) {
    case "GetLog":
      return { logType: "DiagnosticsLog", requestId: 1, retries: 1, retryInterval: 10, log: { remoteLocation: EXAMPLE_UPLOAD_URL } };
    case "UpdateFirmware":
      return {
        requestId: 1,
        retries: 1,
        retryInterval: 10,
        firmware: { location: EXAMPLE_FIRMWARE_URL, retrieveDateTime: now.toISOString() },
      };
    case "PublishFirmware":
      // MD5 of the example firmware response is not known in advance: a mismatch shows InvalidChecksum.
      return { location: EXAMPLE_FIRMWARE_URL, checksum: "00000000000000000000000000000000", requestId: 1, retries: 1, retryInterval: 10 };
    case "RequestStartTransaction":
      return { evseId: evse?.id ?? 1, remoteStartId: 1, idToken };
    case "RequestStopTransaction":
      return { transactionId: tx?.id ?? "" };
    case "GetTransactionStatus":
      return tx ? { transactionId: tx.id } : {};
    case "Reset":
      return { type: "OnIdle" };
    case "SetVariables":
      return {
        setVariableData: [{ component: { name: "SampledDataCtrlr" }, variable: { name: "TxUpdatedInterval" }, attributeValue: "30" }],
      };
    case "GetVariables":
      return {
        getVariableData: [
          { component: { name: "OCPPCommCtrlr" }, variable: { name: "HeartbeatInterval" } },
          { component: { name: "SampledDataCtrlr" }, variable: { name: "TxUpdatedMeasurands" } },
        ],
      };
    case "GetBaseReport":
      return { requestId: 1, reportBase: "FullInventory" };
    case "ChangeAvailability":
      return { operationalStatus: "Inoperative", evse: { id: evse?.id ?? 1 } };
    case "UnlockConnector":
      return { evseId: evse?.id ?? 1, connectorId: 1 };
    case "TriggerMessage":
      return { requestedMessage: "StatusNotification", evse: { id: evse?.id ?? 1, connectorId: 1 } };
    case "ReserveNow":
      return { id: 1, expiryDateTime: inMinutes(15), idToken, evseId: evse?.id ?? 1 };
    case "CancelReservation":
      return { reservationId: evse?.reservation?.id ?? 1 };
    case "SetChargingProfile":
      return {
        evseId: evse?.id ?? 1,
        chargingProfile: {
          id: 1,
          stackLevel: 0,
          chargingProfilePurpose: tx ? "TxProfile" : "TxDefaultProfile",
          chargingProfileKind: "Relative",
          ...(tx && { transactionId: tx.id }),
          chargingSchedule: [{ id: 1, chargingRateUnit: "A", chargingSchedulePeriod: [{ startPeriod: 0, limit: 10, numberPhases: 3 }] }],
        },
      };
    case "GetCompositeSchedule":
      return { duration: 3600, evseId: evse?.id ?? 1, chargingRateUnit: "W" };
    case "SendLocalList":
      return {
        versionNumber: sim.localList.version + 1,
        updateType: "Full",
        localAuthorizationList: [{ idToken, idTokenInfo: { status: "Accepted" } }],
      };
    case "DataTransfer":
      return { vendorId: "EVsafe", messageId: "Ping", data: "{}" };
    default:
      return undefined;
  }
};
